import type { ChangeEventHandler } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Box, FormHelperText, FormLabel, Group, Textarea, Typography } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { PublicationSettingUpdateInput } from 'data/generated/graphql';
import PublicationSettingsActionBar from 'features/publication-settings-common/PublicationSettingsActionBar';
import usePublicationSettingsLock from 'features/publication-settings-common/usePublicationSettingsLock';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings, useSavePublicationSetting } from 'hooks/publication-settings';
import { useJsonField } from 'hooks/useJsonField';

const PublicationSettingsPubcrawl = () => {
  const [currentPublicationSetting, setCurrentPublicationSetting] = useState<PublicationSettingUpdateInput | null>(
    null
  );
  const [hasChanged, setHasChanged] = useState(false);

  const currentProperty = useConvertedProperty();
  const { alertError, alertSuccess } = useAlert();
  const { publishingFragment, lockIsReady, lockLoading, isPageLockingEnabled, handleLock, handleRelease } =
    usePublicationSettingsLock();

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data, isLoading: isLoad, fetchStatus } = usePublicationSettings(publicationSettingsQueryVariables);
  const isLoading = isLoad && fetchStatus !== 'idle';

  // The save puts the saved settings in the cache, so the form reloads from them, with their new version.
  const { mutate, isLoading: isUpdating } = useSavePublicationSetting();

  const pubcrawlSectionIdMapping = useJsonField(data?.publicationSetting.pubcrawlSectionIdMapping);

  const jsonFields = useMemo(() => [pubcrawlSectionIdMapping], [pubcrawlSectionIdMapping]);

  useEffect(() => {
    if (data) {
      setCurrentPublicationSetting(data.publicationSetting);
    }
  }, [data]);

  const handleJsonFieldChange =
    (jsonField: { handleChange: (value: string) => void }): ChangeEventHandler<HTMLTextAreaElement> =>
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      jsonField.handleChange((e.target as HTMLTextAreaElement).value);
      setHasChanged(true);
    };

  const buildPublicationSettingInput = (): PublicationSettingUpdateInput => ({
    ...currentPublicationSetting!,
    pubcrawlSectionIdMapping: pubcrawlSectionIdMapping.parseValue()?.getJSON()
  });

  const handleSave = () => {
    if (currentPublicationSetting === null || !currentProperty) {
      return;
    }

    let newPublicationSetting: PublicationSettingUpdateInput;
    try {
      newPublicationSetting = buildPublicationSettingInput();
    } catch (err) {
      alertError(`Cannot save — fix the highlighted JSON field(s) first. (${(err as Error).message})`);
      return;
    }

    mutate(
      {
        publicationSettingUpdateInput: newPublicationSetting
      },
      {
        onSuccess: () => {
          setHasChanged(false);
          jsonFields.forEach((field) => field.clearError());
          alertSuccess('Publication settings saved successfully');
        },
        onError: (error) => {
          alertError(`Failed to save publication settings. ${(error as Error).message}`);
        }
      }
    );
  };

  const { isReadOnly } = publishingFragment;

  return (
    <Group gap="lg" direction="column">
      {!currentPublicationSetting ? (
        <ResponsiveLoader />
      ) : (
        <>
          <Group.Item fullWidth>
            <PublicationSettingsActionBar
              onSave={handleSave}
              hasChanged={hasChanged}
              isSaving={isUpdating}
              isLoading={isLoading}
              publishingFragment={publishingFragment}
              lockIsReady={lockIsReady}
              isPageLockingEnabled={isPageLockingEnabled}
              lockLoading={lockLoading}
              onLock={handleLock}
              onRelease={handleRelease}
            />
          </Group.Item>

          <Group.Item fullWidth>
            <Box>
              <Box.Title>Pubcrawl section ID mapping</Box.Title>
              <Box.Content>
                <Typography variant="note">
                  JSON object mapping UCS section IDs to Pubcrawl section IDs. Required for any section that renders in
                  the mobile app.
                </Typography>
                <Group gap="mlg" direction="column" fullWidth margin={{ top: 'md' }}>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Pubcrawl Section ID Mapping" labelPosition="top" fullWidth />
                    <FormHelperText>
                      Keys are UCS section IDs, values are the Pubcrawl section IDs the mobile app expects.
                    </FormHelperText>
                    <Textarea
                      style={{ height: 800 }}
                      placeholder="Enter the pubcrawl section ID mapping as JSON"
                      value={pubcrawlSectionIdMapping.stringValue}
                      onChange={handleJsonFieldChange(pubcrawlSectionIdMapping)}
                      error={!!pubcrawlSectionIdMapping.error}
                      disabled={isReadOnly}
                    />
                    {!!pubcrawlSectionIdMapping.error && (
                      <FormHelperText error>{pubcrawlSectionIdMapping.error}</FormHelperText>
                    )}
                  </Group>
                </Group>
              </Box.Content>
            </Box>
          </Group.Item>
        </>
      )}
    </Group>
  );
};

export default PublicationSettingsPubcrawl;
